import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash2, Minus, Plus, CalendarIcon } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, money, formatApiError } from "@/lib/api";
import { formatKcal } from "@/lib/dietary";
import { format } from "date-fns";
import { toast } from "sonner";

const PICKUP_TIMES = [
  "17:30", "18:00", "18:30", "19:00", "19:30",
  "20:00", "20:30", "21:00", "21:30",
];

export default function CartSheet({ open, onOpenChange }) {
  const { lines, updateQuantity, removeLine, clear, totalAmount, totalItems, totalCalories } = useCart();
  const [step, setStep] = useState("cart");
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [paymentMethod, setPaymentMethod] = useState("pay_on_pickup");
  const [splitPayment, setSplitPayment] = useState("none");
  const [seats, setSeats] = useState(1);
  const [seatAssignments, setSeatAssignments] = useState([]);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [checkoutUrls, setCheckoutUrls] = useState([]);

  // Per-seat calorie preview (used in by-seat + per-item flow)
  const seatCaloriesPreview = (() => {
    if (splitPayment === "none") return [];
    const arr = new Array(Math.max(1, seats)).fill(0);
    if (splitPayment === "equal") {
      const per = totalCalories / Math.max(1, seats);
      return arr.map(() => Math.round(per));
    }
    lines.forEach((l, idx) => {
      const seatIdx = Math.max(0, Math.min(seats - 1, seatAssignments[idx] ?? 0));
      arr[seatIdx] += (l.calories || 0) * l.quantity;
    });
    return arr.map((n) => Math.round(n));
  })();

  function close() {
    onOpenChange(false);
  }

  async function submitOrder() {
    if (!date || !time) {
      toast.error("Please pick a pickup date and time");
      return;
    }
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your contact details");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        pickup_date: format(date, "yyyy-MM-dd"),
        pickup_time: time,
        notes: form.notes,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
        split_payment: splitPayment,
        seats: splitPayment === "none" ? 1 : seats,
        seat_assignments: splitPayment === "per_item" ? seatAssignments : [],
        items: lines.map((l) => ({
          item_id: l.item_id,
          quantity: l.quantity,
          selected_addon_options: l.selected_addons.map((a) => a.name),
          notes: l.notes || "",
          price_column: l.price_column || "",
        })),
      };
      const { data } = await api.post("/orders", payload);
      if (data.checkout_urls && data.checkout_urls.length > 1) {
        // Split payment: keep the cart visible with a list of seat-pay links
        setCheckoutUrls(data.checkout_urls);
        setConfirmedOrder(data.order);
        setStep("split-pay");
        toast.success(`${data.checkout_urls.length} payment links created`);
        return;
      }
      if (data.checkout_url) {
        toast.success("Redirecting to payment…");
        window.location.href = data.checkout_url;
        return;
      }
      setConfirmedOrder(data.order);
      setStep("success");
      clear();
      toast.success("Pre-order placed!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="cart-sheet"
        className="w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="p-6 border-b border-[#E8E5DF]">
          <SheetTitle className="font-serif-display text-2xl">
            {step === "success"
              ? "Order confirmed"
              : step === "checkout"
              ? "Pickup details"
              : "Your order"}
          </SheetTitle>
        </SheetHeader>

        {step === "cart" && (
          <div className="flex-1 overflow-y-auto p-6">
            {lines.length === 0 ? (
              <div data-testid="cart-empty" className="text-center py-16 text-[#5C625E]">
                <p className="font-serif-display text-2xl text-[#121614]">Your basket is empty</p>
                <p className="mt-2 text-sm">Browse the menu to start your pre-order.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {lines.map((l, i) => (
                  <div
                    key={i}
                    data-testid={`cart-line-${i}`}
                    className="border border-[#E8E5DF] rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-2">
                        <p className="font-medium text-[#121614]">
                          {l.name}
                          {l.price_label && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#5A7247] bg-[#5A7247]/10 px-1.5 py-0.5 rounded">
                              {l.price_label}
                            </span>
                          )}
                        </p>
                        {l.selected_addons.length > 0 && (
                          <ul className="mt-1 text-xs text-[#5C625E] space-y-0.5">
                            {l.selected_addons.map((a, k) => (
                              <li key={k}>
                                + {a.name} {a.price > 0 ? `(${money(a.price)})` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                        {l.notes && (
                          <p className="mt-1 text-xs italic text-[#5C625E]">"{l.notes}"</p>
                        )}
                      </div>
                      <button
                        type="button"
                        data-testid={`cart-remove-${i}`}
                        onClick={() => removeLine(i)}
                        className="h-8 w-8 rounded-full hover:bg-[#F3F0EA] flex items-center justify-center"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-4 w-4 text-[#5C625E]" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid={`cart-qty-dec-${i}`}
                          onClick={() => updateQuantity(i, l.quantity - 1)}
                          className="h-8 w-8 rounded-full border border-[#E8E5DF] flex items-center justify-center"
                          aria-label="Decrease"
                        >
                          <Minus className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                        <span className="w-6 text-center text-sm">{l.quantity}</span>
                        <button
                          type="button"
                          data-testid={`cart-qty-inc-${i}`}
                          onClick={() => updateQuantity(i, l.quantity + 1)}
                          className="h-8 w-8 rounded-full border border-[#E8E5DF] flex items-center justify-center"
                          aria-label="Increase"
                        >
                          <Plus className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-medium" data-testid={`cart-line-total-${i}`}>
                          {money(l.line_total)}
                        </p>
                        {l.calories > 0 && (
                          <p
                            className="text-[10px] text-[#5C625E]"
                            data-testid={`cart-line-calories-${i}`}
                          >
                            ≈ {formatKcal(l.calories * l.quantity)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "checkout" && (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <Label htmlFor="co-name">Full name</Label>
              <Input
                id="co-name"
                data-testid="checkout-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="co-email">Email</Label>
              <Input
                id="co-email"
                data-testid="checkout-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="co-phone">Phone</Label>
              <Input
                id="co-phone"
                data-testid="checkout-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-2 rounded-xl"
              />
            </div>
            <div>
              <Label>Pickup date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="checkout-date-trigger"
                    className="w-full mt-2 justify-start text-left rounded-xl border-[#E8E5DF] font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    {date ? format(date, "PPP") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Pickup time</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {PICKUP_TIMES.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`checkout-time-${t}`}
                    onClick={() => setTime(t)}
                    className={`h-10 rounded-xl text-sm border transition-colors ${
                      time === t
                        ? "bg-[#C05746] text-white border-[#C05746]"
                        : "bg-white border-[#E8E5DF] hover:border-[#121614]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="co-notes">Notes (optional)</Label>
              <Input
                id="co-notes"
                data-testid="checkout-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 rounded-xl"
              />
            </div>
            <div>
              <Label>Order mode</Label>
              <RadioGroup
                value={splitPayment === "none" ? "table" : "by_seat"}
                onValueChange={(v) => {
                  if (v === "table") setSplitPayment("none");
                  else if (splitPayment === "none") setSplitPayment("per_item");
                }}
                className="mt-2 space-y-2"
              >
                <label
                  data-testid="mode-table"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                    splitPayment === "none" ? "border-[#5A7247] bg-[#5A7247]/5" : "border-[#E8E5DF]"
                  }`}
                >
                  <RadioGroupItem value="table" id="mode-table-input" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Order as a table</p>
                    <p className="text-xs text-[#5C625E]">One bill, single payment.</p>
                  </div>
                </label>
                <label
                  data-testid="mode-by-seat"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                    splitPayment !== "none" ? "border-[#5A7247] bg-[#5A7247]/5" : "border-[#E8E5DF]"
                  }`}
                >
                  <RadioGroupItem value="by_seat" id="mode-by-seat-input" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Order by seat</p>
                    <p className="text-xs text-[#5C625E]">
                      See ≈ calories per seat and pay one seat at a time.
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>
            <div>
              <Label>Payment</Label>
              <RadioGroup
                value={paymentMethod}
                onValueChange={setPaymentMethod}
                className="mt-2 space-y-2"
              >
                <label
                  data-testid="pay-pickup-option"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                    paymentMethod === "pay_on_pickup"
                      ? "border-[#C05746] bg-[#C05746]/5"
                      : "border-[#E8E5DF]"
                  }`}
                >
                  <RadioGroupItem value="pay_on_pickup" id="pay-pickup" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Pay on pickup</p>
                    <p className="text-xs text-[#5C625E]">Cash or card at the restaurant.</p>
                  </div>
                </label>
                <label
                  data-testid="pay-stripe-option"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                    paymentMethod === "stripe"
                      ? "border-[#C05746] bg-[#C05746]/5"
                      : "border-[#E8E5DF]"
                  }`}
                >
                  <RadioGroupItem value="stripe" id="pay-stripe" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Pay now with card (Stripe)</p>
                    <p className="text-xs text-[#5C625E]">
                      {splitPayment !== "none"
                        ? "Generates one Stripe link per seat."
                        : "Secure card or Apple Pay."}
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>
            <div>
              <Label>Bill split (when ordering by seat)</Label>
              <RadioGroup
                value={splitPayment}
                onValueChange={setSplitPayment}
                disabled={splitPayment === "none"}
                className="mt-2 space-y-2"
              >
                <label
                  data-testid="split-equal"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                    splitPayment === "equal" ? "border-[#5A7247] bg-[#5A7247]/5" : "border-[#E8E5DF]"
                  } ${splitPayment === "none" ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <RadioGroupItem value="equal" id="split-equal-input" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Split equally per seat</p>
                    <p className="text-xs text-[#5C625E]">
                      Each guest gets their own payment link for an equal share.
                    </p>
                  </div>
                </label>
                <label
                  data-testid="split-per-item"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                    splitPayment === "per_item" ? "border-[#5A7247] bg-[#5A7247]/5" : "border-[#E8E5DF]"
                  } ${splitPayment === "none" ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <RadioGroupItem value="per_item" id="split-per-item-input" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Per-item assignment</p>
                    <p className="text-xs text-[#5C625E]">
                      Assign each line to a seat and see calories per seat.
                    </p>
                  </div>
                </label>
              </RadioGroup>
              {splitPayment !== "none" && (
                <div className="mt-3 p-3 bg-[#F3F0EA] rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Number of seats</Label>
                    <Input
                      data-testid="split-seats"
                      type="number"
                      min="2"
                      max="30"
                      className="h-9 w-20 rounded-lg"
                      value={seats}
                      onChange={(e) =>
                        setSeats(Math.max(2, Math.min(30, parseInt(e.target.value) || 2)))
                      }
                    />
                  </div>
                  {splitPayment === "equal" && (
                    <p className="text-xs text-[#5C625E]">
                      Each seat will pay {money(totalAmount / Math.max(seats, 1))} ·{" "}
                      ≈ {formatKcal(totalCalories / Math.max(seats, 1))}
                    </p>
                  )}
                  {splitPayment === "per_item" && (
                    <div className="space-y-2">
                      <p className="text-xs text-[#5C625E]">
                        Assign each line to a seat (1–{seats}):
                      </p>
                      {lines.map((l, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="text-sm truncate flex-1">{l.name}</span>
                          <Input
                            data-testid={`seat-assign-${i}`}
                            type="number"
                            min="1"
                            max={seats}
                            className="h-8 w-16 rounded-lg"
                            value={(seatAssignments[i] ?? 0) + 1}
                            onChange={(e) => {
                              const v =
                                Math.max(1, Math.min(seats, parseInt(e.target.value) || 1)) - 1;
                              setSeatAssignments((prev) => {
                                const cp = [...prev];
                                cp[i] = v;
                                return cp;
                              });
                            }}
                          />
                        </div>
                      ))}
                      <SeatPreview
                        lines={lines}
                        seats={seats}
                        seatAssignments={seatAssignments}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {step === "split-pay" && confirmedOrder && (
          <div data-testid="cart-split-pay" className="flex-1 overflow-y-auto p-6 space-y-3">
            <p className="font-serif-display text-2xl">Pay per seat</p>
            <p className="text-sm text-[#5C625E]">
              Order #{confirmedOrder.id.slice(0, 8)} · share each link with the right guest.
            </p>
            <ul className="space-y-2 mt-2">
              {checkoutUrls.map((url, i) => {
                const cals = seatCaloriesPreview[i] || 0;
                return (
                  <li
                    key={i}
                    className="border border-[#E8E5DF] rounded-xl p-3 flex items-center justify-between gap-2"
                  >
                    <div>
                      <p className="text-sm font-medium">Seat {i + 1}</p>
                      <p className="text-xs text-[#5C625E]">
                        {money(confirmedOrder.seat_totals?.[i] || 0)}
                        {cals > 0 && <> · ≈ {formatKcal(cals)}</>}
                      </p>
                    </div>
                    <a
                      data-testid={`split-pay-link-${i}`}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-[#C05746] hover:text-[#A64A3C] font-medium"
                    >
                      Pay →
                    </a>
                  </li>
                );
              })}
            </ul>
            <Button
              data-testid="split-pay-close"
              onClick={() => {
                close();
                clear();
              }}
              className="w-full mt-4 bg-[#121614] text-white hover:bg-black rounded-full"
            >
              Done
            </Button>
          </div>
        )}

        {step === "success" && confirmedOrder && (
          <div className="flex-1 overflow-y-auto p-6 text-center">
            <div className="mt-6">
              <p className="font-serif-display text-3xl">Grazie!</p>
              <p className="mt-3 text-[#5C625E]">
                Your order #{confirmedOrder.id.slice(0, 8)} is in. Pickup on{" "}
                <strong>{confirmedOrder.pickup_date}</strong> at{" "}
                <strong>{confirmedOrder.pickup_time}</strong>.
              </p>
              <Button
                data-testid="cart-success-close"
                onClick={close}
                className="mt-8 bg-[#121614] text-white hover:bg-black rounded-full px-8"
              >
                Close
              </Button>
            </div>
          </div>
        )}

        {step !== "success" && (
          <SheetFooter className="p-6 border-t border-[#E8E5DF] bg-white">
            {step === "cart" ? (
              <div className="w-full space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#5C625E]">Subtotal ({totalItems})</span>
                  <span data-testid="cart-subtotal" className="text-xl font-medium">
                    {money(totalAmount)}
                  </span>
                </div>
                <Button
                  data-testid="cart-checkout-button"
                  onClick={() => setStep("checkout")}
                  disabled={lines.length === 0}
                  className="w-full bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-12"
                >
                  Continue to Pickup
                </Button>
              </div>
            ) : (
              <div className="w-full space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#5C625E]">Total</span>
                  <span className="text-xl font-medium">{money(totalAmount)}</span>
                </div>
                <Button
                  data-testid="checkout-place-order"
                  onClick={submitOrder}
                  disabled={submitting}
                  className="w-full bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-12"
                >
                  {submitting
                    ? "Placing…"
                    : paymentMethod === "stripe"
                    ? "Continue to payment"
                    : "Place Pre-Order"}
                </Button>
                <button
                  type="button"
                  data-testid="checkout-back"
                  onClick={() => setStep("cart")}
                  className="w-full text-sm text-[#5C625E] hover:text-[#121614]"
                >
                  ← Back to cart
                </button>
              </div>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}


function SeatPreview({ lines, seats, seatAssignments }) {
  const breakdown = [];
  for (let s = 0; s < seats; s++) {
    let amt = 0;
    let cals = 0;
    const items = [];
    lines.forEach((l, idx) => {
      const seatIdx = seatAssignments[idx] ?? 0;
      if (seatIdx === s) {
        amt += l.line_total;
        cals += (l.calories || 0) * l.quantity;
        items.push(`${l.quantity}× ${l.name}`);
      }
    });
    breakdown.push({ s, amt, cals, items });
  }
  return (
    <div data-testid="seat-preview" className="mt-3 space-y-1">
      {breakdown.map((b) => (
        <div
          key={b.s}
          data-testid={`seat-preview-${b.s}`}
          className="flex items-start justify-between text-xs bg-white border border-[#E8E5DF] rounded-lg p-2"
        >
          <div className="flex-1 pr-2">
            <p className="font-medium text-[#121614]">Seat {b.s + 1}</p>
            <p className="text-[10px] text-[#5C625E] line-clamp-2">
              {b.items.length ? b.items.join(" · ") : "(no items)"}
            </p>
          </div>
          <div className="text-right">
            <p className="font-medium">
              {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(b.amt)}
            </p>
            {b.cals > 0 && <p className="text-[10px] text-[#5C625E]">≈ {Math.round(b.cals)} kcal</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
